'use client';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '@repo/ui/src/utils/classNames';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useState } from 'react';

export interface NavListProps {
  navItems: Array<{ headerText?: string; title?: string }>;
  visibleSection: number;
  setVisibleSection: (section: number) => void;
}

const NavList = ({ navItems, visibleSection, setVisibleSection }: NavListProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const labels = navItems?.map((item) => item.headerText ?? item.title);

  return (
    <div>
      <div className="tw-hidden md:tw-block tw-mb-10 tw-sticky tw-top-20 tw-bg-white tw-border tw-border-warm-400 tw-mt-40 tw-max-h-[calc(100vh-6rem)] tw-overflow-auto">
        <nav className="tw-grid tw-px-8 tw-py-10 tw-gap-6">
          <h5 className="tra-text-web-heading-5">
            {t('NavListContent.sectionText', undefined, { default: 'Sections' })}
          </h5>
          <ul className="tw-px-0 tw-grid tw-gap-5 tw-list-none">
            {labels.map((label, index) => {
              // we can't filter these out ahead of time because it screws up the indexing
              if (!label) {
                return null;
              }
              return (
                <li key={label}>
                  <Anchor
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById(`${index}header`);
                      if (el) {
                        const above = window.scrollY < el.offsetTop;
                        window.scrollTo({
                          top: el.offsetTop + (above ? 1 : -1),
                          behavior: 'smooth',
                        });
                      }
                    }}
                    link={`#${index}header`}
                    className={classNames(
                      'tra-text-web-body-l hover:tw-text-brand-400',
                      visibleSection === index && 'tw-font-bold',
                    )}
                  >
                    {label}
                  </Anchor>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      <div className="md:tw-hidden tw-relative">
        <button
          className="tw-py-4 tw-px-10 tw-flex tw-justify-between tw-items-center tw-bg-white tw-w-full tw-rounded tw-border-2 tw-border-neutral-800"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          <h5 className="tra-text-web-utl-button-l">
            {t('NavListContent.sectionText', undefined, { default: 'Sections' })}
          </h5>
          <span className="tw-w-7 tw-inline-block">
            <ChevronIcon direction={open ? 'up' : 'down'} />
          </span>
        </button>
        {open && (
          <div className="tw-max-h-[50vh] tw-overflow-auto tw-absolute tw-border-2 tw-border-neutral-700">
            <ul className="tw-list-none tw-grid tw-auto-rows-fr tw-px-0  tw-bg-white tw-border-l-2 tw-border-r-2 tw-border-neutral-800 tw-w-full">
              {labels.map((label, index) => {
                // we can't filter these out ahead of time because it screws up the indexing
                if (!label) {
                  return null;
                }
                return (
                  <li
                    key={label}
                    className="tw-py-4 tw-px-5 tw-border-b tw-border-warm-400 tw-grid tw-content-center"
                  >
                    <Anchor
                      onClick={() => {
                        setVisibleSection(index);
                        setOpen(false);
                      }}
                      link={`#${index}header`}
                      className="tra-text-web-body-xl hover:tw-text-brand-400"
                    >
                      {label}
                    </Anchor>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default NavList;
